/**
 * RadarTileLayer.tsx — طبقة الرادار (‏NOAA MRMS، خمسة نطاقات).
 *
 * ── لماذا `image` لا `raster`؟ ────────────────────────────────────────────────
 *
 * كان المصدر بلاطاتٍ من RainViewer، وكان يظهر عند التكبير الشديد بلاطاتٌ مكتوب
 * عليها «Zoom Level Not Supported» — وهي **صورة PNG صالحة** يردّها المزوّد فوق
 * سقفه المقيس (‏z=7)، لا خطأ شبكة، فلا يمنعها أي معالج أخطاء.
 *
 * مصدر `image` يزيل الفئة كلها لا الحالة: **لا مستويات تكبير فيه أصلاً**. صورة
 * واحدة مثبَّتة بأركانها الأربعة، يستويها الـGPU خطّياً عند أي تقريب. فلا يوجد
 * «مستوى غير مدعوم» يُطلب، لأنه لا تُطلب مستويات. وهذا هو سلوك Zoom Earth البصري:
 * يضبب بنعومة عند التكبير الشديد ولا يعتذر بلافتة.
 *
 * وشرط صحّته أن تكون الصورة **مُسقَطة بمِركاتور سلفاً** — لأن الاستيفاء بين
 * الأركان خطّي في فضاء مِركاتور لا في خطوط العرض. المولّد يفعل ذلك
 * (‏`to_mercator_max` في mrms_to_raster.py)؛ ولو أُهمل لانزاح الرادار ~235 كم في
 * منتصف الصورة. راجع `projection` في البيان.
 *
 * ── لماذا مصدر لكل نطاق؟ ──────────────────────────────────────────────────────
 *
 * MRMS ينشر خمس شبكات منفصلة (القارّة، ألاسكا، الكاريبي، هاواي، غوام) لا شبكة
 * عالمية واحدة، ولكلٍّ حدودها ودقّتها (هاواي وغوام ‏550 متراً مقابل 1.1 كم للقارّة).
 * دمجها في صورة واحدة كان سيفرض إطاراً عالمياً فارغاً بنسبة 97% ويهبط بالجميع إلى
 * أخشن دقّة. فالعدد يأتي من البيان لا من ثابت هنا: نطاقٌ سادس ينشره المولّد يظهر
 * على الخريطة بلا تعديل في هذا الملفّ.
 *
 * ── قناع التغطية ──────────────────────────────────────────────────────────────
 *
 * خريطةٌ خاليةٌ من المطر وخريطةٌ لا رادار فيها تبدوان **متطابقتين** بلا هذا القناع —
 * وهما نقيضان: الأولى تقول «صحوٌ مؤكَّد»، والثانية تقول «لا أعلم». عرضُ الثانية
 * كأنها الأولى ليس نقصاً في العرض بل معلومةٌ خاطئة.
 *
 * لذلك نرسم تحت الصدى قناعاً يُفتّح ما يراه الرادار فعلاً. وهو مشتقّ من علم `-999`
 * في MRMS نفسه، لا من تقدير هندسي لقباب المحطّات — فيتبع أعطال المحطّات وصيانتها
 * تلقائياً بدل أن يعرض تغطيةً نظرية لا وجود لها اليوم.
 *
 * ── التحريك: الشريط الزمني هو المحرّك ────────────────────────────────────────
 *
 * كان هنا مؤقّتٌ خاصّ بهذه الطبقة، مستقلّ عن الشريط عمداً: الشريط كان محوره ساعات
 * توقّع النموذج (‏0..24 ساعة **قادمة**) بينما إطارات الرادار ماضيةٌ متباعدة بدقيقتين،
 * فربطهما بالفهرس — كما فعل كود RainViewer عبر `currentTimeIndex % frames.length` —
 * يُنتج مطابقةً بلا معنى: الموضع على الشريط لا يدلّ على وقت الإطار المعروض.
 *
 * لكن ذلك حلَّ التضارب بإلغاء الشريط للرادار: يسحبه المستخدم فلا يتحرّك شيء. والعلاج
 * الصحيح أن يُبدَّل **المحور** لا أن يُلغى — وهو ما يفعله التطبيق أصلاً مع القمر
 * الصناعي والمحيط. فصار للرادار محوره في `useRadarFrames`، علاماته أوقات الرصد
 * الحقيقية، وصارت هذه الطبقة تختار إطارها **بالزمن المطلق** لا بفهرس.
 *
 * وربحان يتبعان ذلك مجّاناً: مؤقّتٌ واحد في التطبيق (‏`useAutoplay`) بدل اثنين يتسابقان
 * على نفس الطبقة، وزرُّ التشغيل ومؤشّر الوقت يقولان الصدق عن الرادار كما يقولانه عن
 * غيره.
 *
 * والاختيار **بالزمن لا بالفهرس**: النطاقات لا تتساوى (‏MRMS كل دقيقتين، OPERA كل خمس،
 * وتعثّرُ تنزيلٍ يترك نطاقاً بإطارات أقلّ)، ففهرسٌ مشترك كان سيعرض لحظتين مختلفتين جنباً
 * إلى جنب على خريطة واحدة. راجع `RadarDomainFrames` أسفل الملفّ للمزج بين الإطارين.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Source, Layer } from 'react-map-gl/maplibre';
import { useWeatherStore } from '../../store/weatherStore';
import { frameAtEpoch, framesAround } from './hooks/useRadarFrames';
import { subscribePlayback } from '../../services/playbackClock';
import { useMapRef } from './MapContext';
import { getRadarInsertBeforeId } from './webgl/layerOrder';
import { RadarDomainTiles } from './RadarDomainTiles';
import { RadarValuesGL } from './RadarValuesGL';
import type { RadarFlowField } from './webgl/RadarFlowGLLayer';
import { isRasterBytesReady, prefetchRasterBytes } from '../../services/rasterImageCache';
import { radarDebugInflight } from '../../services/radarDebug';
import {
    domainInView, preloadRadarFrames, RADAR_COVERAGE_MAX_Z, radarCoordinates, radarCoverageUrl,
    radarFrameUrl, radarTilesTemplate, radarViewTileUrls,
    type RadarDomain, type RadarManifest,
} from '../../services/mrmsRadarService';

interface Props {
    /** بيان الأرشيف — يأتي من `useRadarFrames` في الأعلى، مشتركاً مع محور الشريط. */
    manifest: RadarManifest | null;
    /**
     * الفسيفساء حين تكون منشورة: نطاقٌ واحد يحمل كل المصادر مدموجة.
     *
     * حين توجد، يُرسم الصدى منها وحدها — مصدرٌ واحد وطبقتان بدل عشرين مصدراً
     * وثمانين طبقة. وقِيس ما تكلّفه العشرون في 2026-09-20: حلقة رسم MapLibre
     * وحدها 34 مللي لكل إطار، أي سقفٌ عند ثلاثين إطاراً في الثانية.
     *
     * وأقنعة التغطية تبقى من البيان القديم: هي صورٌ ساكنة لا تتحرّك مع الحلقة،
     * وكل قناع يقول «هنا رادار يرى» — معلومةٌ لكل نطاق على حدة بطبيعتها.
     */
    mosaic?: RadarDomain | null;
    /**
     * بادئة بلاطات قناع التغطية — **مصدر واحد** بدل صورةٍ كاملة لكل نطاق.
     *
     * القناع ظلّ آخر ما يُرسم بالطريقة القديمة: ستّة أو سبعة مصادر `image` فوق
     * أوروبا. وقِيس أثرها في 2026-09-20: بإطفاء القناع خرج MapLibre من قمّة
     * الحجز، وبإبقائه كان ~50 مللي لكل إطار.
     */
    coverageTiles?: string | null;
    /** حقل الحركة للانزلاق بين الإطارات (مع بلاطات القيم). */
    flow?: RadarFlowField | null;
    /** اللحظة المعروضة على الشريط (epoch بالثواني). */
    displayEpoch: number;
    /** علامات المحور — يُحمّى ما يقع عليها فقط لا الأرشيف كلّه (‏60 إطاراً لـMRMS). */
    times: readonly number[];
}

export function RadarTileLayer({ manifest, mosaic, coverageTiles, displayEpoch, times, flow = null }: Props) {
    const visible = useWeatherStore((s) => s.visibleLayers.radar);
    // حدود الشاشة: لا نُركّب إلّا النطاقات التي تلامسها (راجع domainInView).
    const mapBounds = useWeatherStore((s) => s.mapBounds);
    const zoomLevel = useWeatherStore((s) => s.zoomLevel);
    const isPlaying = useWeatherStore((s) => s.isPlaying);
    const coverageOn = useWeatherStore((s) => s.radarCoverageEnabled);

    /*
     * ── مرساة الإدراج: الخطوط فوق الألوان ────────────────────────────────────
     *
     * بلا `beforeId` تُلحَق طبقات react-map-gl في **قمّة** المكدّس، فيطمس الصدى
     * الطرقَ وحدود المقاطعات والتسميات — وهي المعلومة التي تجعل البقعة الحمراء
     * تعني «فوق هذه المدينة» لا مجرّد لون. المرجع (WeatherFront) يرسمها فوق الصدى.
     *
     * تُقرأ بالاستطلاع لا مرّةً واحدة: النمط قد لا يكون جاهزاً عند أوّل تركيب،
     * و`mapRef` كائنٌ ثابت لا يتغيّر مرجعه فلا يُعاد تشغيل المؤثّر حين يجهز — وهو
     * نفس الفخّ الذي وقعت فيه طبقة البروق (راجع GlmLightningLayer).
     */
    const mapRef = useMapRef();
    const [beforeId, setBeforeId] = useState<string | undefined>(undefined);
    useEffect(() => {
        if (!visible) return;
        let id = 0;
        const read = (): boolean => {
            const gl = mapRef.current?.getMap();
            // «محلَّل» لا «محمَّل» — راجع نفس البوّابة في RadarDomainTiles: مع
            // الصورة الجوّية تبقى isStyleLoaded() خاطئةً ما دام أحدٌ ينظر.
            const parsed = !!(gl as unknown as { style?: { _loaded?: boolean } } | undefined)?.style?._loaded;
            if (!gl || !(parsed || gl.isStyleLoaded())) return false;
            setBeforeId(getRadarInsertBeforeId(gl));
            return true;
        };
        if (!read()) id = window.setInterval(() => { if (read()) window.clearInterval(id); }, 200);
        return () => window.clearInterval(id);
    }, [visible, mapRef]);

    // ── الإحماء عند التشغيل فقط ──────────────────────────────────────────────
    //
    // من يفتح الرادار لينظر لحظةً لا ينبغي أن يدفع ثمن حلقةٍ قد لا يشغّلها.
    useEffect(() => {
        if (!visible || !isPlaying || !manifest) return;
        if (mosaic) return;          // لا إطارات كاملة مع الفسيفساء
        const inView = manifest.domains.filter((d) => domainInView(d.bounds, mapBounds));
        const isTiled = (d: RadarDomain) => d.frames.length > 0 && d.frames.every((f) => !!f.tiles);
        // غير المقطَّع يبقى على مساره القديم: الإطار الكامل/المصغَّر.
        const stop = inView.filter((d) => !isTiled(d))
            .map((d) => preloadRadarFrames([...new Set(times.map((t) => frameAtEpoch(d, t)))]));

        return () => { for (const fn of stop) fn(); };
    }, [visible, isPlaying, manifest, mosaic, times, mapBounds]);

    /*
     * ── إحماء البلاطات ───────────────────────────────────────────────────────
     *
     * الشريط الأزرق يقرأ `isRasterBytesReady`، وهي لا تعرف إلا ما مرّ بكاش الراسترات.
     * وبعد التقطيع لم يعد أحد يطلب شيئاً عبره — فبقي الشريط رماديّاً رغم أن الطبقة
     * تعمل (رُصد 2026-09-20).
     *
     * ── ولماذا متوازياً ومن المؤشّر ─────────────────────────────────────────
     * أوّل نسخةٍ من هذا الإحماء كانت حلقةً متسلسلة تنتظر كل طلب قبل التالي: ستّون
     * إطاراً × حتى عشرين نطاقاً = ألفٌ ومئتا رحلة شبكة واحدةً بعد واحدة، أي أكثر من
     * دقيقة حتى يمتلئ الخطّ. والبلاطة نفسها مئات البايتات — فالكلفة كانت في **زمن
     * الرحلة** لا في الحجم، وعلاجها التوازي لا الضغط ولا الكاش.
     *
     * والترتيب من المؤشّر إلى الخارج (الأمام أوّلاً): هو ما سيُعرض فعلاً بعد لحظة.
     * البدء من أوّل المحور يجعل المستخدم ينتظر إحماء ماضٍ لن ينظر إليه — وهو نفس
     * مبدأ طابور الإحماء في NewWeatherMap.
     */
    /*
     * ── الإحماء: عمّالٌ دائمون يقرأون موضع المؤشّر، لا مؤثّرٌ يُعاد تشغيله ──────
     *
     * جعلتُ الترتيب تبعيّةً على موضع المؤشّر فصار المؤثّر يُعاد تشغيله عشر مرّات في
     * الثانية (المؤشّر يتقدّم بذلك الإيقاع)، وكل مرّة تُطلق عمّالاً جدداً بينما القدامى
     * يُنهون طلباتهم. النتيجة مقيسة على جهاز المستخدم في 2026-09-20: **84 طلباً
     * متوازياً** رغم حدّ الثلاثة، و**29 ثانية من الحجز في 26.8 ثانية قياس** — أي أن
     * الخيط الرئيسي كان مشغولاً بالكامل ببناء قوائم روابط لا بعرض الرادار.
     *
     * فالعمّال هنا ثلاثة يعيشون طول عمر الطبقة، ويسألون **مرجعاً** عن موضع المؤشّر
     * قبل كل طلب. فتتبع الأولوية المؤشّر بلا إعادة تشغيل، ويبقى المتوازي ثلاثة مهما
     * تحرّك. (ما حُمّي سلفاً يعود فوراً من `prefetchRasterBytes` بلا رحلة شبكة.)
     */
    const atRef = useRef(0);
    atRef.current = useMemo(() => {
        let best = 0;
        for (let i = 1; i < times.length; i++) {
            if (Math.abs(times[i] - displayEpoch) < Math.abs(times[best] - displayEpoch)) best = i;
        }
        return best;
    }, [times, displayEpoch]);

    useEffect(() => {
        if (!visible || !manifest || !times.length) return;
        // الفسيفساء نطاقٌ واحد يغطّي العالم — تُحمّى وحدها، فلا معنى لإحماء نطاقات
        // لم تعد تُرسم أصلاً.
        const tiled = mosaic ? [mosaic] : manifest.domains
            .filter((d) => domainInView(d.bounds, mapBounds))
            .filter((d) => d.frames.length > 0 && d.frames.every((f) => !!f.tiles));
        if (!tiled.length) return;

        // نافذة قصيرة حول المؤشّر: ما سيُعرض خلال ثوانٍ. الأبعد منها يُحمّى حين يقترب
        // المؤشّر منه، فلا نزاحم طلبات الإطار المعروض الآن (راجع قياس 10.1 ثانية).
        const WINDOW = 8;
        const done = new Set<string>();
        let cancelled = false;

        /*
         * ── القائمة تُبنى مرّة لكل موضع، لا مرّة لكل رابط ──────────────────────
         *
         * كانت `nextUrl` تولّد روابط النافذة كلّها — ثمانية إطارات في كل اتّجاه ×
         * مستويين × حتى 64 بلاطة — لتُرجع رابطاً واحداً، ثم تبدأ من الصفر للتالي.
         * كان ذلك محتملاً وسقف البلاطات ثمانٍ؛ رفعُه إلى 64 (لإحماء المستوى المعروض
         * فعلاً) جعلها آلاف الروابط لكل طلب. تتبّع 2026-09-21: 1038 مللي لهذه الحلقة
         * وحدها، بعد أن زالت البنود الأكبر التي كانت تخفيها.
         *
         * والترتيب لا يتغيّر إلا حين ينتقل المؤشّر إلى إطارٍ آخر، فالقائمة تُعاد عندها
         * وحدها، ويتقدّم عليها مؤشّر. وما سُحب سلفاً يبقى في `done` فلا يُطلب مرّتين.
         */
        let listAt = -1;
        let list: string[] = [];
        let pos = 0;
        const buildList = (at: number) => {
            const out: string[] = [];
            const seen = new Set<string>();
            for (let d = 0; d <= WINDOW; d++) {
                for (const i of d === 0 ? [at] : [at + d, at - d]) {
                    if (i < 0 || i >= times.length) continue;
                    for (const dom of tiled) {
                        const f = frameAtEpoch(dom, times[i]);
                        if (!f) continue;
                        // الأخشن أوّلاً ثم الدقيق: الأخشن ربع العدد ويكفي لتقدّم
                        // الحركة (هو ما تقيسه الجاهزية)، والدقيق يلحق به ليتّضح العرض.
                        for (const zz of [zoomLevel - 1, zoomLevel]) {
                            for (const u of radarViewTileUrls(dom, f, mapBounds, Math.max(0, zz))) {
                                if (!seen.has(u)) { seen.add(u); out.push(u); }
                            }
                        }
                    }
                }
            }
            list = out; pos = 0; listAt = at;
        };
        const nextUrl = (): string | null => {
            const at = atRef.current;
            if (at !== listAt) buildList(at);
            while (pos < list.length) {
                const u = list[pos++];
                if (!done.has(u)) { done.add(u); return u; }
            }
            return null;
        };

        const worker = async () => {
            while (!cancelled) {
                const u = nextUrl();
                // لا شيء في النافذة الآن — ننتظر تحرّك المؤشّر بدل أن ندور فارغين.
                if (!u) { await new Promise((r) => setTimeout(r, 400)); continue; }
                radarDebugInflight(1);
                await prefetchRasterBytes(u);
                radarDebugInflight(-1);
            }
        };
        void Promise.all(Array.from({ length: 3 }, worker));
        return () => { cancelled = true; };
    }, [visible, manifest, mosaic, times, mapBounds, zoomLevel]);

    if (!visible || !manifest) return null;

    const epoch = Math.floor(displayEpoch);
    /*
     * حدود المتجر تُنشَر عند تحميل الخريطة، والطبقة قد تُركَّب قبل ذلك (البيان يصل من
     * الشبكة بلا انتظار الخريطة). ولو قصصنا على `null` لَمُنع كل شيء، ولو تركناه بلا
     * قصٍّ لانطلقت دفعةٌ أولى بكل النطاقات — وهي التي قِيست: خمسة وخمسون ملفّاً عند
     * أوّل فتح فوق إيطاليا. فنقرأ حدود الخريطة من المحرّك مباشرةً عند غياب المنشورة.
     */
    const gl = mapRef.current?.getMap();
    let view = mapBounds;
    if (!view && gl) {
        try {
            const b = gl.getBounds();
            view = { north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest() };
        } catch { /* الخريطة قيد الإقلاع — نبقى بلا قصّ لهذه الدورة */ }
    }
    const domains = manifest.domains.filter((d) => domainInView(d.bounds, view));

    return (
        <>
            {/* التغطية أوّلاً: مصادر MapLibre تُرسم بترتيب إضافتها، فوضعها قبل الصدى
                يضمن بقاءها تحته بلا حاجة إلى beforeId يدوي. */}
            {/* القناع المقطَّع: مصدرٌ واحد. والقديم يبقى للسقوط إليه حين لا تُنشر
                بلاطاته بعد — فلا تختفي المعلومة أثناء الترقية. */}
            {coverageOn && coverageTiles && (
                <Source id="radar-coverage-mosaic" type="raster"
                    tiles={[radarTilesTemplate(coverageTiles)]}
                    tileSize={256} minzoom={0} maxzoom={RADAR_COVERAGE_MAX_Z}>
                    <Layer
                        id="radar-coverage-layer-mosaic"
                        beforeId={beforeId}
                        type="raster"
                        paint={{ 'raster-opacity': 1, 'raster-fade-duration': 0 }}
                        layout={{ visibility: 'visible' }}
                    />
                </Source>
            )}

            {coverageOn && !coverageTiles && domains.map((domain) => domain.coverage && (
                <Source
                    key={`${domain.id}-coverage`}
                    id={`radar-coverage-${domain.id}`}
                    type="image"
                    url={radarCoverageUrl(domain.coverage)}
                    coordinates={radarCoordinates(domain.bounds)}
                >
                    <Layer
                        id={`radar-coverage-layer-${domain.id}`}
                        beforeId={beforeId}
                        type="raster"
                        paint={{ 'raster-opacity': 1, 'raster-fade-duration': 0 }}
                        layout={{ visibility: 'visible' }}
                    />
                </Source>
            ))}

            {/*
              * البلاطات أو الصورة الكاملة — والقرار **لكل نطاق**، بشرط أن تكون
              * لإطاراته كلّها بلاطات. فالطبقتان تتقاسمان نفس معرّفات المصادر
              * والطبقات، وتبديلهما وسط الحلقة كان سيتنازع عليها. والشرط الصارم
              * يجعل النطاق يعبر مرّةً واحدة حين يكتمل أرشيفه المقطَّع، بدل أن
              * يتذبذب بين الوضعين مع كل إطار قديم يمرّ.
              */}
            {/*
              * بلاطات القيم تُعرض بطبقة WebGL على طريقة Windy (قيمٌ تُلوَّن على المعالج،
              * وانزلاقٌ بحقل حركة)، وبلاطات الألوان تبقى على الخانات للمقارنة (‏?radarValues=0).
              */}
            {mosaic?.values
                ? <RadarValuesGL key="mosaic-gl" domain={mosaic} times={times} flow={flow} beforeId={beforeId} />
                : mosaic
                ? <RadarDomainTiles key="mosaic" domain={mosaic} times={times} beforeId={beforeId} />
                : domains.map((domain) => (
                    domain.frames.length > 0 && domain.frames.every((f) => !!f.tiles)
                        ? <RadarDomainTiles key={domain.id} domain={domain}
                            times={times} beforeId={beforeId} />
                        : <RadarDomainFrames key={domain.id} domain={domain} epoch={epoch} times={times}
                            playing={isPlaying} beforeId={beforeId} />
                ))}
        </>
    );
}

/**
 * نطاقٌ واحد بإطارَين متراكبَين — **المزج الزمني**.
 *
 * ── ما العلّة التي يُصلحها ───────────────────────────────────────────────────
 * مصدر `image` لا يعرف التلاشي: تغيير رابطه استبدالٌ فوري، و`raster-fade-duration`
 * لا يعمل إلا على البلاطات. فكان الرادار يقفز بين لقطتين متباعدتين خمس دقائق بينما
 * كل ما حوله ينساب — القمر الصناعي يمزج إطارين بنفس الكسر (‏SatelliteTileLayer)،
 * وطبقات النموذج تستوي بين ساعتين في الشيدر، والبرق زمنه متّصل أصلاً.
 *
 * فنُقيم هنا ما يقيمه القمر الصناعي: طبقتان فوق بعضهما، السفلى الإطار السابق والعليا
 * التالي، وشفافيّتهما تتبادلان مع الكسر — ذوبانٌ متقاطع (‏cross-dissolve) لا قفزة.
 *
 * ── لماذا (1−k) و(k) لا (1) و(k) ────────────────────────────────────────────
 * القمر يُبقي السفلى عند 1 لأن صوره معتمة تُغطّي بعضها. وصور الرادار **شفّافة حيث لا
 * صدى**: لو بقيت السفلى عند 1 لظلّت العاصفة المنقضية ظاهرةً من تحت الجديدة حتى نهاية
 * المقطع، فيبدو الصدى ممتدّاً لا متحرّكاً. فالسفلى تخفت بقدر ما تسطع العليا.
 *
 * ── ولماذا الانتظار حتى تجهز الصورة ────────────────────────────────────────
 * لو مزجنا نحو صورة لم تُنزَّل بعد لرأينا الصدى **يتلاشى إلى العدم** ثم يعود دفعةً —
 * أسوأ من القفزة. فنُحمّي التالية، ولا يبدأ المزج إلا حين تصير بايتاتها في الكاش.
 */
/** وقت إطارٍ بالثواني، أو null إن غاب — نفس قراءة `framesAround`. */
function frameEpochOf(f: { time: string | null } | undefined | null): number | null {
    const ms = f?.time ? Date.parse(f.time) : NaN;
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}


function RadarDomainFrames({ domain, epoch, times, playing, beforeId }: {
    domain: RadarDomain; epoch: number; times: readonly number[];
    playing: boolean; beforeId: string | undefined;
}) {
    const mapRef = useMapRef();
    const { cur, next, blend } = useMemo(() => framesAround(domain, epoch), [domain, epoch]);
    // مرجعٌ ثابت ما لم تتغيّر الحدود: مصفوفة جديدة كل إطار تعني `setCoordinates` ستّين
    // مرّة في الثانية على مصدرٍ لم يتحرّك.
    const coords = useMemo(() => radarCoordinates(domain.bounds), [domain.bounds]);

    /*
     * ── المصغَّرة أثناء الحركة، والكاملة عند السكون ──────────────────────────
     *
     * الفارق ليس في التنزيل بل في **فكّ الترميز والرفع إلى الـGPU**: 16.8 ميغابكسل
     * مقابل 1، أي سُدس عشر الكلفة. وهي مقايضة في محلّها: العين لا تقرأ تفاصيل خليّة
     * وهي تمرّ في جزء من الثانية، وتقرؤها حين تقف. فما إن يتوقّف التشغيل حتى يعود
     * الإطار الكامل بدقّته.
     */
    const curUrl = cur ? radarFrameUrl(cur, playing) : null;
    const nextUrl = next ? radarFrameUrl(next, playing) : null;

    useEffect(() => { if (nextUrl) void prefetchRasterBytes(nextUrl); }, [nextUrl]);

    const idA = `radar-layer-${domain.id}`;      // الاسم القديم يبقى: مرساة ترتيب معروفة
    const idB = `radar-layer-${domain.id}-next`;

    // الشفافية تُضبط **أمرياً** لا عبر خصائص React: الكسر يتغيّر مع كل إطار رسم، وتمريره
    // خاصّيةً يُعيد بناء وصف الطبقة ستّين مرّة في الثانية لكل نطاق.
    /*
     * ── المزج يتبع **ساعة التشغيل** لا تحديثات React ─────────────────────────
     *
     * كان هذا المؤثّر مفتاحه `blend` الآتي من خاصّية `epoch` — أي من حالة React. وقياس
     * تسجيلٍ للشاشة في 2026-09-19 حسم الأمر: 380 إطاراً في 18.8 ثانية، **32 منها فقط**
     * تحمل تغيّراً حقيقياً. أي أن الخريطة تُحدَّث مرّتين في الثانية وتبقى بينهما ساكنة
     * بكسلاً ببكسل — شرائح متتابعة لا ذوبان، مهما كانت الصور جاهزة.
     *
     * وطبقات الألوان لا تعاني ذلك لأنها تشترك في `subscribePlayback` وتضبط قيمة المزج
     * كل نبضة رسم متجاوزةً React (راجع RasterHeatmapWebGLLayer). فنفعل هنا مثلها:
     * نشتقّ اللحظة من الساعة نفسها، ونعيد حساب الكسر من **أوقات الإطارات الحقيقية**،
     * ونضبط الشفافيّتين مباشرةً. والروابط تبقى من React — فهي لا تتغيّر إلا عند حدود
     * المقطع، وهو تغيّرٌ قليل يحتمل دورة رسم.
     *
     * وإن اختلف الإطار الحالي عمّا هو مركَّب (عبَرنا حدّ المقطع قبل أن يلحق React)
     * نُثبّت الشفافية على النهاية بدل أن نحسب كسراً لزوجٍ لم يُعرض — وإلّا ومض الصدى
     * رجوعاً إلى الخلف لنبضة واحدة، وهي نفس الفجوة التي يعالجها `blendFor`.
     */
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map || !curUrl) return;
        const apply = (k: number) => {
            try {
                if (map.getLayer(idA)) map.setPaintProperty(idA, 'raster-opacity', 1 - k);
                if (map.getLayer(idB)) map.setPaintProperty(idB, 'raster-opacity', k);
            } catch { /* الطبقة قيد الإضافة أو الحذف — النبضة التالية تضبطها */ }
        };
        const ready = () => !!nextUrl && isRasterBytesReady(nextUrl);
        return subscribePlayback((p) => {
            if (!ready()) { apply(0); return; }
            const i = Math.max(0, Math.min(times.length - 1, p.index));
            const a = times[i];
            if (a === undefined) { apply(blend); return; }
            const b = times[Math.min(i + 1, times.length - 1)] ?? a;
            const now = a + (b - a) * p.fraction;
            const live = framesAround(domain, Math.floor(now));
            // الزوج المركَّب هو المرجع: إن تجاوزته الساعة فالمزج مكتمل، وإن لم تبلغه بعد
            // فصفر — لا نحسب كسراً على زوجٍ غير معروض.
            if (live.cur?.file !== cur?.file) { apply(now > (frameEpochOf(cur) ?? now) ? 1 : 0); return; }
            apply(live.blend);
        });
    }, [mapRef, blend, cur, curUrl, nextUrl, idA, idB, domain, times]);

    if (!curUrl) return null;
    return (
        <>
            <Source id={`radar-source-${domain.id}`} type="image" url={curUrl} coordinates={coords}>
                <Layer
                    id={idA}
                    beforeId={beforeId}
                    type="raster"
                    paint={{
                        // ‏1 لا 0.85: الشفافية مبنيّة في التدرّج أصلاً (الصدى الضعيف شفّاف من
                        // الصورة نفسها)، و0.85 فوقها كانت تخلط خلفية القمر بكل لون فتبدو
                        // الألوان باهتة غير مشبعة حتى في قلب العاصفة. والقيمة هنا نقطة
                        // البداية فقط: المزج الزمني أعلاه يضبطها بعد التركيب.
                        'raster-opacity': 1,
                        // صفر: تلاشي MapLibre بين الإطارات لا يعمل على مصادر الصور أصلاً،
                        // والذوبان المتقاطع أعلاه يتكفّل بالانسياب.
                        'raster-fade-duration': 0,
                    }}
                    layout={{ visibility: 'visible' }}
                />
            </Source>
            {nextUrl && (
                <Source id={`radar-source-${domain.id}-next`} type="image" url={nextUrl} coordinates={coords}>
                    <Layer
                        id={idB}
                        beforeId={beforeId}
                        type="raster"
                        paint={{ 'raster-opacity': 0, 'raster-fade-duration': 0 }}
                        layout={{ visibility: 'visible' }}
                    />
                </Source>
            )}
        </>
    );
}
